import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import SystemStatusCard from "../components/SystemStatusCard";
import SiteDiagnosticsCard from "../components/SiteDiagnosticsCard";
import AdminManualsCard from "../components/AdminManualsCard";
import { API } from "../constants";

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [verifiedPassword, setVerifiedPassword] = useState('');
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPage, setNewPage] = useState({ page_name: '', route: '' });
  const editorRef = useRef(null);
  const editorInstance = useRef(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/verify-password`, { password });
      setIsAuthenticated(true);
      setVerifiedPassword(password);
      loadPages();
    } catch { toast.error("Invalid password"); }
  };

  const loadPages = async () => {
    setIsLoading(true);
    try { const res = await axios.get(`${API}/admin/pages`); setPages(res.data.pages); }
    catch { toast.error("Failed to load pages"); }
    finally { setIsLoading(false); }
  };

  const handleCreatePage = async (e) => {
    e.preventDefault();
    if (!newPage.page_name || !newPage.route) { toast.error("Name and route required"); return; }
    const route = newPage.route.startsWith('/') ? newPage.route : '/' + newPage.route;
    try {
      await axios.post(`${API}/admin/pages`, { page_name: newPage.page_name, route });
      toast.success("Page created");
      setNewPage({ page_name: '', route: '' });
      setShowCreateForm(false);
      loadPages();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create page"); }
  };

  const openEditor = async (page) => {
    setEditingPage(page);
    setShowEditor(true);
    // Wait for DOM and load GrapesJS
    setTimeout(async () => {
      if (editorRef.current && !editorInstance.current) {
        try {
          const grapesjs = (await import('grapesjs')).default;
          await import('grapesjs/dist/css/grapes.min.css');

          editorInstance.current = grapesjs.init({
            container: editorRef.current,
            height: '70vh',
            width: 'auto',
            storageManager: false,
            panels: { defaults: [] },
            canvas: {
              styles: ['https://cdn.tailwindcss.com'],
            },
            components: page.content?.html || '<div class="p-8"><h1 class="text-3xl font-bold">Edit your page here</h1><p class="mt-4 text-[#E8E4DC]">Drag and drop components to build your page.</p></div>',
            style: page.content?.css || '',
            blockManager: {
              appendTo: '#gjs-blocks',
              blocks: [
                { id: 'section', label: 'Section', category: 'Layout', content: '<section class="py-12 px-4"><div class="max-w-4xl mx-auto"><h2 class="text-2xl font-bold mb-4">Section Title</h2><p class="text-[#E8E4DC]">Section content goes here.</p></div></section>' },
                { id: 'heading', label: 'Heading', category: 'Basic', content: '<h2 class="text-3xl font-bold">Heading Text</h2>' },
                { id: 'text', label: 'Text', category: 'Basic', content: '<p class="text-base text-gray-700">Your text content here.</p>' },
                { id: 'image', label: 'Image', category: 'Media', content: '<img src="https://placehold.co/600x400" class="w-full rounded-lg" />' },
                { id: 'button', label: 'Button', category: 'Basic', content: '<a href="#" class="inline-block bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600">Click Me</a>' },
                { id: 'columns-2', label: '2 Columns', category: 'Layout', content: '<div class="grid grid-cols-2 gap-6"><div class="p-4 bg-gray-50 rounded"><p>Column 1</p></div><div class="p-4 bg-gray-50 rounded"><p>Column 2</p></div></div>' },
                { id: 'columns-3', label: '3 Columns', category: 'Layout', content: '<div class="grid grid-cols-3 gap-6"><div class="p-4 bg-gray-50 rounded"><p>Column 1</p></div><div class="p-4 bg-gray-50 rounded"><p>Column 2</p></div><div class="p-4 bg-gray-50 rounded"><p>Column 3</p></div></div>' },
                { id: 'hero', label: 'Hero Banner', category: 'Sections', content: '<div class="bg-gradient-to-r from-green-400 to-blue-500 py-20 px-8 text-center text-white"><h1 class="text-5xl font-bold mb-4">Hero Title</h1><p class="text-xl mb-8 opacity-90">Subtitle text goes here</p><a href="#" class="bg-white text-gray-800 px-8 py-3 rounded-full font-bold">Get Started</a></div>' },
                { id: 'card', label: 'Card', category: 'Components', content: '<div class="bg-white rounded-xl shadow-lg p-6 max-w-sm"><h3 class="text-lg font-bold mb-2">Card Title</h3><p class="text-[#E8E4DC] text-sm">Card description text.</p></div>' },
                { id: 'divider', label: 'Divider', category: 'Basic', content: '<hr class="my-8 border-gray-200" />' },
              ]
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error("GrapesJS init error:", err);
          toast.error("Failed to load editor");
        }
      }
    }, 200);
  };

  const saveEditor = async () => {
    if (!editorInstance.current || !editingPage) return;
    const html = editorInstance.current.getHtml();
    const css = editorInstance.current.getCss();
    try {
      await axios.put(`${API}/admin/pages/${editingPage.page_id}`, { content: { html, css } });
      toast.success("Page saved");
      loadPages();
    } catch { toast.error("Failed to save page"); }
  };

  const closeEditor = () => {
    if (editorInstance.current) {
      editorInstance.current.destroy();
      editorInstance.current = null;
    }
    setShowEditor(false);
    setEditingPage(null);
  };

  const handlePublish = async (pageId) => { try { await axios.post(`${API}/admin/pages/${pageId}/publish`); toast.success("Page published"); loadPages(); } catch { toast.error("Failed"); } };
  const handleUnpublish = async (pageId) => { try { await axios.post(`${API}/admin/pages/${pageId}/unpublish`); toast.success("Page unpublished"); loadPages(); } catch { toast.error("Failed"); } };
  const handleDelete = async (pageId) => { if (!window.confirm("Delete this page?")) return; try { await axios.delete(`${API}/admin/pages/${pageId}`); toast.success("Deleted"); loadPages(); } catch { toast.error("Failed"); } };

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4">
          <div className="card-glass p-8 w-full max-w-md" data-testid="admin-login">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl font-bold text-white">Admin Access</h1>
              <p className="text-[#E8E4DC]">Enter Password to Continue</p>
            </div>
            <form onSubmit={handleLogin}>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark w-full mb-4 text-center" placeholder="Password" data-testid="admin-password" />
              <button type="submit" className="btn-primary w-full" data-testid="admin-submit">Access Admin</button>
            </form>
          </div>
        </div>
      </Layout>
    );
  }

  if (showEditor && editingPage) {
    return (
      <Layout>
        <div className="min-h-[calc(100vh-8rem)] py-6 px-4">
          <div className="max-w-full mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="font-heading text-2xl font-bold text-white">Editing: {editingPage.page_name}</h1>
                <p className="text-[#E8E4DC] text-sm">Route: {editingPage.route}</p>
              </div>
              <div className="flex gap-3">
                <button onClick={saveEditor} className="btn-primary px-6" data-testid="editor-save">Save Page</button>
                <button onClick={closeEditor} className="btn-secondary px-6" data-testid="editor-close">Close Editor</button>
              </div>
            </div>
            <div className="grid grid-cols-[200px_1fr] gap-4">
              <div id="gjs-blocks" className="card-glass p-3 overflow-y-auto max-h-[70vh] rounded-lg" data-testid="editor-blocks"></div>
              <div ref={editorRef} className="rounded-lg overflow-hidden border border-white/10" data-testid="editor-canvas"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-heading text-4xl font-bold text-white" data-testid="admin-title">Admin Dashboard</h1>
            <button onClick={() => setShowCreateForm(!showCreateForm)} className="btn-primary px-6" data-testid="admin-create-btn">
              {showCreateForm ? 'Cancel' : '+ Create Page'}
            </button>
          </div>

          <SystemStatusCard adminPassword={verifiedPassword} />
          <SiteDiagnosticsCard adminPassword={verifiedPassword} />
          <AdminManualsCard adminPassword={verifiedPassword} />

          <div className="card-glass p-6 mb-8" data-testid="stripe-guide-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#A3E635]/10 flex items-center justify-center">
                <span className="text-2xl" aria-hidden="true">💳</span>
              </div>
              <div>
                <h3 className="text-white font-semibold text-base">Stripe Live-Mode Activation Guide</h3>
                <p className="text-[#E8E4DC]/70 text-xs mt-0.5">Click-by-click walkthrough — ~20 min, plain English, no jargon.</p>
              </div>
            </div>
            <a
              href={`${API}/admin/stripe-guide?key=${encodeURIComponent(verifiedPassword || "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-block text-sm px-5 py-2"
              data-testid="open-stripe-guide-btn"
            >
              Open the guide →
            </a>
          </div>

          <h2 className="font-heading text-2xl font-semibold text-white mb-4">Page Manager</h2>

          {showCreateForm && (
            <div className="card-glass p-6 mb-8" data-testid="admin-create-form">
              <h3 className="text-white font-semibold mb-4">Create New Page</h3>
              <form onSubmit={handleCreatePage} className="flex flex-col sm:flex-row gap-4">
                <input type="text" value={newPage.page_name} onChange={(e) => setNewPage({...newPage, page_name: e.target.value})} className="input-dark flex-1" placeholder="Page Name" data-testid="create-page-name" />
                <input type="text" value={newPage.route} onChange={(e) => setNewPage({...newPage, route: e.target.value})} className="input-dark flex-1" placeholder="/route-path" data-testid="create-page-route" />
                <button type="submit" className="btn-primary px-8 whitespace-nowrap" data-testid="create-page-submit">Create</button>
              </form>
            </div>
          )}

          <div className="card-glass overflow-hidden" data-testid="admin-table">
            {isLoading ? <div className="p-8 text-center text-[#E8E4DC]">Loading...</div> : (
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4 text-[#E8E4DC]">Page Name</th>
                    <th className="text-left p-4 text-[#E8E4DC]">Route</th>
                    <th className="text-left p-4 text-[#E8E4DC]">Status</th>
                    <th className="text-left p-4 text-[#E8E4DC]">Version</th>
                    <th className="text-left p-4 text-[#E8E4DC]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-[#E8E4DC]">No custom pages yet. Click "Create Page" to start building.</td></tr>
                  ) : pages.map(page => (
                    <tr key={page.page_id} className="border-t border-white/10">
                      <td className="p-4 text-white">{page.page_name}</td>
                      <td className="p-4 text-[#E8E4DC]">{page.route}</td>
                      <td className={`p-4 ${page.published ? 'text-[#A3E635]' : 'text-yellow-400'}`}>{page.published ? 'Published' : 'Draft'}</td>
                      <td className="p-4 text-[#E8E4DC]">v{page.version}</td>
                      <td className="p-4">
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => openEditor(page)} className="text-[#A3E635] hover:underline text-sm" data-testid={`edit-page-${page.page_id}`}>Edit</button>
                          {page.published
                            ? <button onClick={() => handleUnpublish(page.page_id)} className="text-yellow-400 hover:underline text-sm">Unpublish</button>
                            : <button onClick={() => handlePublish(page.page_id)} className="text-cyan-400 hover:underline text-sm">Publish</button>
                          }
                          <button onClick={() => handleDelete(page.page_id)} className="text-red-400 hover:underline text-sm">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
